/**
 * Shared hook that processes raw courier route data into UI-ready format.
 *
 * Extracts the computation previously in Courier.tsx useEffect so that
 * Courier, CourierOrder, and CourierWarehouse can all derive state from
 * the same React Query cache entry (['courier-routes', today]).
 *
 * Includes an optimistic overlay: pages that mutate order state can write
 * their optimistic ProcessedOrder here so that other pages (e.g. route list)
 * see updates instantly without waiting for a server refetch.
 */

import { useMemo, useContext, useSyncExternalStore, useRef, useEffect, useState } from 'react';
import { DateTime } from 'luxon';

import { useCourierRoutesQuery } from './use-queries';
import useLogisticsApi, { transformOrderBoxToEnhanced, OFFLINE_QUEUE_CHANGED_EVENT } from './use-logistics-api';
import AuthContext from '../store/auth-context';

import {
    Order, ProcessedOrder, EnhancedOrderBox, WorkflowType, Warehouse,
    COLLECTION_PHASE_STATUSES, DELIVERY_PHASE_STATUSES,
} from '../types/logistics';
import { getDeliveryReadiness } from '../utils/delivery-readiness';
import { optimizeRoute, OptimizedRoute, RouteStop, LatLng, buildGoogleMapsEmbedUrl, buildLiveNavigationUrl } from '../utils/route-optimizer';

// ─── Optimistic Stop Overlay ─────────────────────────────────────────────────
// Module-level store for optimistic ProcessedOrder overrides.
// CourierOrder writes here before navigating so Courier sees updates instantly.
// Cleared automatically when server data catches up after refetch.

let _optimisticStops = new Map<string, ProcessedOrder>();
const _listeners = new Set<() => void>();

function _notify() {
    _listeners.forEach(l => l());
}

/** Write an optimistic stop override (call before navigating away). */
export function setOptimisticStop(stop: ProcessedOrder) {
    _optimisticStops = new Map(_optimisticStops);
    _optimisticStops.set(`${stop.type}-${stop.id}`, stop);
    _notify();
}

/** Clear all optimistic overrides (called after server data catches up). */
function clearOptimisticStops() {
    if (_optimisticStops.size === 0) return;
    _optimisticStops = new Map();
    _notify();
}

function _subscribe(listener: () => void) {
    _listeners.add(listener);
    return () => { _listeners.delete(listener); };
}

function _getSnapshot() {
    return _optimisticStops;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ProcessedRouteData {
    stops: ProcessedOrder[];
    load: { boxes: EnhancedOrderBox[]; progress: number };
    unload: { boxes: EnhancedOrderBox[]; progress: number };
    optimizedRoute: OptimizedRoute | null;
    mapEmbedUrl: string | null;
    liveNavUrl: string | null;
    warehouseInfo: Warehouse | null;
    stopOrder: string[] | null;
}

const EMPTY_DATA: ProcessedRouteData = {
    stops: [],
    load: { boxes: [], progress: 0 },
    unload: { boxes: [], progress: 0 },
    optimizedRoute: null,
    mapEmbedUrl: null,
    liveNavUrl: null,
    warehouseInfo: null,
    stopOrder: null,
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useProcessedRoutes() {
    const authCtx = useContext(AuthContext);
    const myUserId = authCtx.user?.id ?? null;
    const today = useMemo(() => DateTime.now().toFormat('yyyy-MM-dd'), []);

    const { calcProgress, applyWorkflowFilter, getPendingSyncBoxIds } = useLogisticsApi();

    const { data: rawRouteData, isLoading, error } = useCourierRoutesQuery(today);

    // Subscribe to the optimistic overlay store
    const optimisticStops = useSyncExternalStore(_subscribe, _getSnapshot);

    // Track pending-sync box ids live so boxes with queued mutations render with a badge.
    const [pendingSyncIds, setPendingSyncIds] = useState<Set<number>>(() => getPendingSyncBoxIds());
    useEffect(() => {
        const update = () => setPendingSyncIds(getPendingSyncBoxIds());
        window.addEventListener(OFFLINE_QUEUE_CHANGED_EVENT, update);
        return () => window.removeEventListener(OFFLINE_QUEUE_CHANGED_EVENT, update);
    }, [getPendingSyncBoxIds]);

    const processed = useMemo((): ProcessedRouteData => {
        if (!rawRouteData) return EMPTY_DATA;

        const { orders: rawOrders, orderCoordinates, warehouse, stopOrder } = rawRouteData;

        const stops: ProcessedOrder[] = [];
        let loadBoxes: EnhancedOrderBox[] = [];
        const unloadBoxes: EnhancedOrderBox[] = [];

        rawOrders.forEach((order: Order) => {
            if (!order.storageRequired) return;

            // Backend filters these out in getCourierRoutes, but guard defensively
            // against stale/cached responses.
            if (order.collectionCompletedAt && order.deliveryCompletedAt) return;

            const enhancedBoxes = order.orderBoxes.map(transformOrderBoxToEnhanced).map((b) => {
                if (pendingSyncIds.has(b.id)) {
                    b.isPendingSync = true;
                }
                return b;
            });
            const hasCollection = !!order.collectionAddress;
            const hasDelivery = !!order.deliveryAddress;

            const todayStart = DateTime.now().startOf('day');
            const collectionDateIsToday = order.collectionDate
                ? DateTime.fromMillis(order.collectionDate).startOf('day').equals(todayStart)
                : false;
            const deliveryDateIsToday = order.deliveryDate
                ? DateTime.fromMillis(order.deliveryDate).startOf('day').equals(todayStart)
                : false;

            const collectionSigned = !!order.collectionCompletedAt;
            const deliverySigned = !!order.deliveryCompletedAt;

            // Courier filter: match on the specific driver field for each leg.
            // collectionDriverId / deliveryDriverId are partner_users.id values
            // that identify which driver handles each leg of the order.
            // No fallback to assignedDriverId — if the specific driver field is
            // NULL the order is unassigned for that leg and must not appear.
            const isMyCollection = myUserId != null && order.collectionDriverId === myUserId;
            const isMyDelivery = myUserId != null && order.deliveryDriverId === myUserId;

            if (hasCollection && collectionDateIsToday && isMyCollection) {
                // Boxes can be in different lifecycle phases on the same order. Only
                // count boxes the driver can actually action in the current phase.
                // Boxes past collection (WKKU, WKKS, WC, etc.) were already collected
                // in a prior trip.
                const collectionBoxes = applyWorkflowFilter([...enhancedBoxes], 'collection' as WorkflowType)
                    .filter(b => COLLECTION_PHASE_STATUSES.includes(b.statusCode));
                const collectionCompleted = collectionBoxes.filter(b => b.isCompleted).length;
                const collectionStop: ProcessedOrder = {
                    ...order, type: 'collection', boxes: collectionBoxes,
                    progress: collectionCompleted, signature: collectionSigned,
                    index: stops.length,
                };
                stops.push(collectionStop);
                unloadBoxes.push(...collectionBoxes);
            }

            // Return collection (collection-back/ONS) is handled by DPD, not internal
            // couriers. Status updates come via DPD tracking webhooks in the backend
            // (ShippingLabelRepository), so these orders should never appear on internal
            // driver routes.

            if (hasDelivery && deliveryDateIsToday && isMyDelivery) {
                const readiness = getDeliveryReadiness(enhancedBoxes);
                const allDeliveryBoxes = applyWorkflowFilter([...enhancedBoxes], 'delivery' as WorkflowType);
                const pendingCount = allDeliveryBoxes.filter(b => ['WKKDO', 'WKKP'].includes(b.statusCode)).length;
                // Boxes can be in different lifecycle phases on the same order. Only
                // count boxes the driver can actually action in the current phase.
                // WCFC (failed collection) boxes never entered the delivery pipeline.
                const deliveryBoxes = allDeliveryBoxes.filter(b => DELIVERY_PHASE_STATUSES.includes(b.statusCode));

                // Skip delivery stop when there's nothing to deliver and nothing in the pipeline.
                // This happens when all boxes failed collection (WCFC) — they're terminal and won't
                // proceed to delivery. Without this, the route shows a permanently blocked stop.
                if (deliveryBoxes.length === 0 && pendingCount === 0 && readiness.state === 'completed') {
                    return; // forEach continue — skip this order's delivery stop
                }

                const deliveryCompleted = deliveryBoxes.filter(b => b.isCompleted).length;
                const deliveryStop: ProcessedOrder = {
                    ...order, type: 'delivery', boxes: deliveryBoxes,
                    progress: deliveryCompleted, signature: deliverySigned,
                    index: stops.length,
                    pendingWarehouseBoxes: pendingCount,
                    deliveryReadiness: readiness,
                    allBoxCount: enhancedBoxes.length,
                };
                stops.push(deliveryStop);
                const loadReadyBoxes = deliveryBoxes.filter(b => b.statusCode === 'WKKHO');
                loadBoxes.push(...loadReadyBoxes);
            }
        });

        const unloadProgress = calcProgress('warehouse-unload', { boxes: unloadBoxes });

        // Build route stops with coordinates for optimization
        const routeStops: RouteStop[] = stops.map((stop, i) => {
            const coords = orderCoordinates?.[stop.id];
            const useCollectionCoords = stop.type === 'collection';
            const lat = useCollectionCoords ? coords?.collectionLatitude : coords?.deliveryLatitude;
            const lng = useCollectionCoords ? coords?.collectionLongitude : coords?.deliveryLongitude;
            return {
                index: i,
                coords: (lat != null && lng != null) ? { lat, lng } : null,
                label: stop.type === 'collection'
                    ? (stop.collectionAddress || '')
                    : (stop.deliveryAddress || ''),
            };
        });

        const warehouseOrigin: LatLng | null =
            (warehouse?.latitude != null && warehouse?.longitude != null)
                ? { lat: warehouse.latitude, lng: warehouse.longitude }
                : null;
        const warehouseAddress = warehouse
            ? [warehouse.address, warehouse.city, warehouse.postcode].filter(Boolean).join(', ')
            : null;

        const optimized = optimizeRoute(warehouseOrigin, routeStops, warehouseAddress);

        let reorderedStops = optimized.orderedIndices.map((origIdx, routeIdx) => ({
            ...stops[origIdx],
            index: routeIdx,
            routeIndex: routeIdx,
        }));

        // ── Apply optimistic overlay ──
        // Replace server-computed stops with any optimistic overrides.
        // This ensures that when CourierOrder mutates and navigates back,
        // the route list shows the updated state instantly.
        if (optimisticStops.size > 0) {
            reorderedStops = reorderedStops.map(stop => {
                const key = `${stop.type}-${stop.id}`;
                const override = optimisticStops.get(key);
                if (override) {
                    // Preserve route-level fields (index, routeIndex) from server computation,
                    // overlay box-level and order-level changes from optimistic state
                    return { ...override, index: stop.index, routeIndex: stop.routeIndex };
                }
                return stop;
            });
        }

        // Rebuild load boxes in optimized order
        loadBoxes = [];
        reorderedStops.forEach(stop => {
            if (stop.type === 'delivery') {
                const loadReadyBoxes = stop.boxes
                    .filter(b => b.statusCode === 'WKKHO')
                    .map(b => ({
                        ...b,
                        routeIndex: stop.routeIndex,
                        orderCustomerName: stop.customer
                            ? `${stop.customer.firstName} ${stop.customer.lastName}`
                            : `Order #${stop.id}`,
                    }));
                loadBoxes.push(...loadReadyBoxes);
            }
        });
        loadBoxes.sort((a, b) => (b.routeIndex ?? 0) - (a.routeIndex ?? 0));
        const finalLoadProgress = calcProgress('warehouse-load', { boxes: loadBoxes });

        const orderedRouteStops = optimized.orderedIndices.map(origIdx => routeStops[origIdx]);

        return {
            stops: reorderedStops,
            load: { boxes: loadBoxes, progress: finalLoadProgress },
            unload: { boxes: unloadBoxes, progress: unloadProgress },
            optimizedRoute: optimized,
            mapEmbedUrl: buildGoogleMapsEmbedUrl(warehouseOrigin, orderedRouteStops),
            liveNavUrl: buildLiveNavigationUrl(orderedRouteStops),
            warehouseInfo: warehouse || null,
            stopOrder,
        };
    }, [rawRouteData, myUserId, calcProgress, applyWorkflowFilter, optimisticStops, pendingSyncIds]);

    // Clear optimistic overrides when rawRouteData changes (server refetch completed).
    // The overlay has already been applied in the useMemo above on the initial render;
    // once the server data catches up (new rawRouteData reference), overlays are stale.
    const prevRawDataRef = useRef(rawRouteData);
    useEffect(() => {
        if (rawRouteData && rawRouteData !== prevRawDataRef.current) {
            prevRawDataRef.current = rawRouteData;
            clearOptimisticStops();
        }
    }, [rawRouteData]);

    return {
        ...processed,
        isLoading,
        error: error as Error | null,
        hasData: !!rawRouteData,
        today,
    };
}
